import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@eudonet/opal';
import { getFilters } from '../infrastructure/adapters/filterAdapter';
import { logger } from '@/infrastructure/services/LoggerService';
import { resolveApiErrorKey } from '@/infrastructure/services/ApiErrorService';
import { SAVED_FILTER_UNAVAILABLE_KEYS } from '../infrastructure/savedFilterErrorCodes';
// A Personal filter the caller cannot manage access to is someone else's, shared with them.
function scopeOf(f) {
    if (f.visibility === 'System')
        return 'eudonet';
    if (f.visibility === 'Workspace')
        return 'admin';
    return f.canManageAccess ? 'myLists' : 'shared';
}
// Module-level cache shared across all useFilters instances for the same entityType.
// All instances read from and write to the same refs, so a reload triggered by one
// (e.g. via invalidateFilters) is immediately visible to all.
const _cache = {
    contact: ref([]),
    organisation: ref([]),
    resident: ref([]),
};
const _loading = {
    contact: ref(false),
    organisation: ref(false),
    resident: ref(false),
};
const _loaded = {
    contact: ref(false),
    organisation: ref(false),
    resident: ref(false),
};
// Shared in-flight promise per entityType. Concurrent load() calls join the same
// request instead of early-returning, so a failed first attempt doesn't leave
// subsequent mounts stuck with filtersReady=false.
const _inflight = {};
// One active reload function per entityType, registered on mount and cleared on unmount.
const _reloaders = new Map();
/**
 * Invalidates the in-memory filter cache and triggers an immediate re-fetch.
 * Call this after any create / modify / delete operation on saved filters.
 * Omit entityType to invalidate all entity caches at once.
 */
export function invalidateFilters(entityType) {
    const types = entityType
        ? [entityType]
        : ['contact', 'organisation', 'resident'];
    for (const type of types) {
        _reloaders.get(type)?.();
    }
}
export function useFilters(entityType, application) {
    const { t } = useI18n();
    const { showToast } = useToast();
    const filters = _cache[entityType];
    const loading = _loading[entityType];
    function filterLabel(f) {
        // BFF system filter names follow the pattern `{entityType}.filters.{key}`;
        // pop() extracts `{key}` to build the i18n path.
        return t(`${entityType}.filters.${f.name.split('.').pop() ?? f.name}`);
    }
    const allLists = computed(() => filters.value
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((f) => ({
        id: f.id,
        label: f.visibility === 'System' ? filterLabel(f) : f.name,
        scope: scopeOf(f),
        isDefault: f.isDefault,
        displayOrder: f.displayOrder,
    })));
    async function load() {
        if (_inflight[entityType])
            return _inflight[entityType];
        const promise = (async () => {
            try {
                loading.value = true;
                filters.value = await getFilters(entityType, application);
            }
            catch (e) {
                logger.error('Failed to load filters', e);
                showToast({
                    message: t(resolveApiErrorKey(e, SAVED_FILTER_UNAVAILABLE_KEYS, 'filters.loadError')),
                    ariaClose: t('form.dismissToast'),
                    status: 'error',
                });
            }
            finally {
                loading.value = false;
                _loaded[entityType].value = true;
                delete _inflight[entityType];
            }
        })();
        _inflight[entityType] = promise;
        return promise;
    }
    onMounted(() => {
        _reloaders.set(entityType, load);
        // Only fetch if the cache is empty — subsequent mounts reuse cached data.
        // invalidateFilters() bypasses this by calling load() directly.
        if ((filters.value?.length ?? 0) === 0) {
            load();
        }
        else {
            _loaded[entityType].value = true;
        }
    });
    onUnmounted(() => {
        if (_reloaders.get(entityType) === load) {
            _reloaders.delete(entityType);
        }
    });
    const filtersReady = _loaded[entityType];
    return { allLists, loading, reload: load, filtersReady };
}
